import React, { useCallback, useMemo, useState } from 'react';

import Papa from 'papaparse';

import { Close, UploadFile } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { getAdminAdverts } from '~/services/MerchandiseService';

import { batchArray } from './utils/utils';

interface BulkUploadDialogProps {
  open: boolean;
  onClose: () => void;
  onApply: (rankings: Array<ParsedRanking>) => void;
}

export interface ParsedRanking {
  rank: number;
  advertId: string;
  // Advert details will be fetched and merged by parent component
}

interface ParsedRow {
  rank: number;
  advertId: string;
  title?: string;
}

interface ValidationResult {
  isValid: boolean;
  errors: Array<string>;
  parsedRows: Array<ParsedRow>;
  hasNonConsecutiveRanks: boolean;
}

const ADVERT_BATCH_SIZE = 100;

// Parse and validate CSV using papaparse
const parseAndValidateCsv = (csvText: string): ValidationResult => {
  const errors: Array<string> = [];

  // Parse CSV with papaparse
  const result = Papa.parse<Record<string, string>>(csvText, {
    header: true,
    skipEmptyLines: true,
    transformHeader: (header) => header.toLowerCase().trim(),
  });

  if (result.errors.length > 0) {
    return {
      isValid: false,
      errors: result.errors.map((e) => `Row ${(e.row ?? 0) + 2}: ${e.message}`),
      parsedRows: [],
      hasNonConsecutiveRanks: false,
    };
  }

  if (result.data.length === 0) {
    return {
      isValid: false,
      errors: ['CSV must have at least one data row'],
      parsedRows: [],
      hasNonConsecutiveRanks: false,
    };
  }

  // Check required columns exist
  const headers = result.meta.fields ?? [];
  if (!headers.includes('rank')) {
    errors.push('Required column "Rank" not found');
  }
  if (!headers.includes('advertid')) {
    errors.push('Required column "AdvertId" not found');
  }

  if (errors.length > 0) {
    return { isValid: false, errors, parsedRows: [], hasNonConsecutiveRanks: false };
  }

  const parsedRows: Array<ParsedRow> = [];
  const seenRanks = new Set<number>();
  const seenAdvertIds = new Set<string>();

  result.data.forEach((row, index) => {
    const rowNum = index + 2; // +2 for 1-based index and header row
    const rankStr = row['rank'];
    const advertId = row['advertid'];
    const title = row['title'];

    // Validate rank
    if (!rankStr || rankStr === '') {
      errors.push(`Row ${rowNum}: Rank is empty`);
      return;
    }

    const rank = parseInt(rankStr, 10);
    if (isNaN(rank) || rank <= 0) {
      errors.push(`Row ${rowNum}: Rank "${rankStr}" is not a valid positive integer`);
      return;
    }

    // Validate advert ID
    if (!advertId || advertId === '') {
      errors.push(`Row ${rowNum}: AdvertId is empty`);
      return;
    }

    // Check for duplicate ranks
    if (seenRanks.has(rank)) {
      errors.push(`Row ${rowNum}: Duplicate rank "${rank}"`);
      return;
    }
    seenRanks.add(rank);

    // Check for duplicate advert IDs
    if (seenAdvertIds.has(advertId)) {
      errors.push(`Row ${rowNum}: Duplicate AdvertId "${advertId}"`);
      return;
    }
    seenAdvertIds.add(advertId);

    parsedRows.push({ rank, advertId, title });
  });

  // Sort by rank
  parsedRows.sort((a, b) => a.rank - b.rank);

  // Check if ranks are consecutive (1, 2, 3, ...)
  const hasNonConsecutiveRanks = parsedRows.length > 0 && !parsedRows.every((row, index) => row.rank === index + 1);

  return {
    isValid: errors.length === 0,
    errors,
    parsedRows,
    hasNonConsecutiveRanks,
  };
};

// Validate that all advert IDs exist in the backend
const validateAdvertsExist = async (
  advertIds: Array<string>,
): Promise<{ valid: boolean; missingIds: Array<string> }> => {
  if (advertIds.length === 0) return { valid: true, missingIds: [] };

  const batches = batchArray(advertIds, ADVERT_BATCH_SIZE);
  const foundIds = new Set<string>();

  // Fetch all batches in parallel for better performance
  const results = await Promise.allSettled(batches.map((batch) => getAdminAdverts(batch)));

  results.forEach((result) => {
    if (result.status === 'fulfilled') {
      result.value.result.adverts.forEach((advert) => foundIds.add(advert.id));
    }
    // If rejected, we can't validate those IDs - they'll appear as missing
  });

  const missingIds = advertIds.filter((id) => !foundIds.has(id));
  return { valid: missingIds.length === 0, missingIds };
};

export default function BulkUploadDialog({ open, onClose, onApply }: BulkUploadDialogProps) {
  const [file, setFile] = useState<File | null>(null);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [advertValidationError, setAdvertValidationError] = useState<string | null>(null);

  const handleFileUpload = useCallback(async (uploadedFile: File) => {
    setFile(uploadedFile);
    setAdvertValidationError(null);
    setIsValidating(true);

    try {
      const text = await uploadedFile.text();
      const result = parseAndValidateCsv(text);
      setValidationResult(result);

      // If basic validation passed, validate advert IDs exist
      if (result.isValid && result.parsedRows.length > 0) {
        const advertIds = result.parsedRows.map((r) => r.advertId);
        const { valid, missingIds } = await validateAdvertsExist(advertIds);

        if (!valid) {
          const displayIds = missingIds.slice(0, 5).join(', ');
          const moreCount = missingIds.length > 5 ? ` and ${missingIds.length - 5} more` : '';
          setAdvertValidationError(`The following advert IDs do not exist: ${displayIds}${moreCount}`);
        }
      }
    } catch (err) {
      setValidationResult({
        isValid: false,
        errors: [`Failed to read file: ${err instanceof Error ? err.message : 'Unknown error'}`],
        parsedRows: [],
        hasNonConsecutiveRanks: false,
      });
    } finally {
      setIsValidating(false);
    }
  }, []);

  const handleClose = useCallback(() => {
    setFile(null);
    setValidationResult(null);
    setAdvertValidationError(null);
    setIsValidating(false);
    onClose();
  }, [onClose]);

  const handleApply = useCallback(() => {
    if (!validationResult || !validationResult.isValid || advertValidationError) return;

    // Re-number ranks to be consecutive (1, 2, 3, ...) based on sorted order
    const rankings: Array<ParsedRanking> = validationResult.parsedRows.map((row, index) => ({
      rank: index + 1,
      advertId: row.advertId,
    }));

    onApply(rankings);
    handleClose();
  }, [validationResult, advertValidationError, onApply, handleClose]);

  // Preview data (re-numbered ranks)
  const previewData = useMemo(() => {
    if (!validationResult?.parsedRows) return [];
    return validationResult.parsedRows.map((row, index) => ({
      ...row,
      newRank: index + 1,
    }));
  }, [validationResult]);

  const hasErrors = !validationResult?.isValid || !!advertValidationError;
  const canApply = validationResult?.isValid && !advertValidationError && !isValidating;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          Bulk Upload Rankings
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Upload a CSV file with <code>Rank</code> and <code>AdvertId</code> columns. This will replace all current
          rankings.
        </Typography>

        <Box mb={3}>
          <Button variant="outlined" component="label" startIcon={<UploadFile />}>
            {file ? file.name : 'Select CSV File'}
            <input
              type="file"
              accept=".csv"
              hidden
              onChange={(e) => {
                const uploadedFile = e.target.files?.[0];
                if (uploadedFile) handleFileUpload(uploadedFile);
                // Reset input so selecting the same file triggers onChange again
                e.target.value = '';
              }}
            />
          </Button>
        </Box>

        {isValidating && (
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <CircularProgress size={20} />
            <Typography variant="body2">Validating...</Typography>
          </Box>
        )}

        {/* Validation Errors */}
        {validationResult && !validationResult.isValid && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Validation failed:
            </Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {validationResult.errors.slice(0, 10).map((error, i) => (
                <li key={i}>{error}</li>
              ))}
              {validationResult.errors.length > 10 && <li>...and {validationResult.errors.length - 10} more errors</li>}
            </ul>
          </Alert>
        )}

        {/* Advert validation error */}
        {advertValidationError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {advertValidationError}
          </Alert>
        )}

        {/* Success message */}
        {validationResult?.isValid && !advertValidationError && !isValidating && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Validation passed! {validationResult.parsedRows.length} rankings ready to apply.
          </Alert>
        )}

        {/* Non-consecutive ranks warning */}
        {validationResult?.isValid && validationResult.hasNonConsecutiveRanks && !isValidating && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Ranks in your CSV are not consecutive. They will be normalized to 1, 2, 3... while preserving the relative
            order.
          </Alert>
        )}

        {/* Preview Table */}
        {previewData.length > 0 && (
          <>
            <Typography variant="subtitle2" gutterBottom>
              Preview (first 10 rows)
            </Typography>
            <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Rank</TableCell>
                    <TableCell>Advert ID</TableCell>
                    <TableCell>Title (from CSV)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewData.slice(0, 10).map((row) => (
                    <TableRow key={row.advertId}>
                      <TableCell>{row.newRank}</TableCell>
                      <TableCell>{row.advertId}</TableCell>
                      <TableCell>{row.title ?? '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {previewData.length > 10 && (
              <Typography variant="caption" color="text.secondary" mt={1} display="block">
                ...and {previewData.length - 10} more rows
              </Typography>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="text">
          Cancel
        </Button>
        <Button variant="contained" color="primary" onClick={handleApply} disabled={!canApply || hasErrors}>
          Apply Rankings
        </Button>
      </DialogActions>
    </Dialog>
  );
}
